import { useState } from "react";
import {
  List,
  DataTable,
  ReferenceField,
  CreateButton,
} from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import { useRecordContext, useListContext } from "ra-core";
import {
  Briefcase,
  FileText,
  DollarSign,
  Activity,
  Calendar,
  Hash,
  CheckCircle,
} from "lucide-react";
import type { Invoice } from "../../providers/fakerest/dataGenerator/types";
import { TopToolbar } from "../../layout/TopToolbar";
import {
  FilterSidebar,
  FilterButton,
  type FilterSection,
  type FilterOption,
} from "../../filters/FilterSidebar";

// Column header with icon helper
const ColHeader = ({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) => (
  <span className="flex items-center gap-1.5">
    <Icon className="h-4 w-4 text-muted-foreground" />
    {label}
  </span>
);

const statusColors: Record<string, string> = {
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  overdue: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const StatusBadge = (record: Invoice) => {
  const status = record.status;
  return (
    <Badge
      className={
        statusColors[status] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
      }
    >
      {status}
    </Badge>
  );
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const AmountCell = (record: Invoice) => (
  <span className="font-medium">{formatCurrency(record.amount)}</span>
);

const JobAddress = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span className="truncate max-w-[200px]">{record.address}</span>;
};

const ProposalTotal = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>#{record.id}</span>;
};

const PaidAtCell = (record: Invoice) => {
  if (!record.paid_at) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="text-green-600 dark:text-green-400">
      {new Date(record.paid_at).toLocaleDateString()}
    </span>
  );
};

// Filter configuration
const statusSection: FilterSection = {
  id: "status",
  label: "Status",
  icon: <Activity className="h-4 w-4" />,
  type: "multi",
  source: "status",
  options: [
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
    { value: "overdue", label: "Overdue" },
  ],
};

const filterSections: FilterSection[] = [statusSection];

const sortOptions: FilterOption[] = [
  { value: "created_at:DESC", label: "Newest first" },
  { value: "created_at:ASC", label: "Oldest first" },
  { value: "amount:DESC", label: "Highest amount" },
  { value: "amount:ASC", label: "Lowest amount" },
  { value: "due_date:ASC", label: "Due soon" },
  { value: "paid_at:DESC", label: "Recently paid" },
];

const InvoiceListActions = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const { filterValues } = useListContext();

  const activeFilterCount = Object.entries(filterValues || {}).filter(
    ([key, value]) => key !== "q" && value !== undefined && value !== "",
  ).length;

  return (
    <>
      <TopToolbar>
        <FilterButton
          onClick={() => setFilterOpen(true)}
          activeCount={activeFilterCount}
        />
        <CreateButton />
      </TopToolbar>
      <FilterSidebar
        open={filterOpen}
        onOpenChange={setFilterOpen}
        sections={filterSections}
        sortOptions={sortOptions}
      />
    </>
  );
};

export const InvoiceList = () => (
  <List actions={<InvoiceListActions />} perPage={1000} pagination={false}>
    <DataTable<Invoice> rowClick="show">
      <DataTable.Col
        source="id"
        label={<ColHeader icon={Hash} label="ID" />}
        headerClassName="w-[60px]"
        cellClassName="w-[60px]"
      />
      <DataTable.Col
        label={<ColHeader icon={Briefcase} label="Job" />}
        headerClassName="w-[300px]"
        cellClassName="w-[300px]"
      >
        <ReferenceField source="job_id" reference="jobs" link={false}>
          <JobAddress />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col
        label={<ColHeader icon={FileText} label="Proposal" />}
        headerClassName="w-[100px]"
        cellClassName="w-[100px]"
      >
        <ReferenceField source="proposal_id" reference="proposals" link={false}>
          <ProposalTotal />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col
        source="amount"
        label={<ColHeader icon={DollarSign} label="Amount" />}
        render={AmountCell}
        headerClassName="text-right w-[120px]"
        cellClassName="text-right w-[120px]"
      />
      <DataTable.Col
        source="status"
        label={<ColHeader icon={Activity} label="Status" />}
        render={StatusBadge}
        headerClassName="w-[100px]"
        cellClassName="w-[100px]"
      />
      <DataTable.Col
        source="due_date"
        label={<ColHeader icon={Calendar} label="Due Date" />}
        headerClassName="w-[120px]"
        cellClassName="w-[120px]"
      />
      <DataTable.Col
        source="paid_at"
        label={<ColHeader icon={CheckCircle} label="Paid At" />}
        render={PaidAtCell}
        headerClassName="w-[120px]"
        cellClassName="w-[120px]"
      />
    </DataTable>
  </List>
);
